import { Injectable, Inject } from '@angular/core';
import { Meta, MetaDefinition } from '@angular/platform-browser';
import { IUProperty } from '../shared/interface/umbraco-property';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AppConfigService } from './app-config.service';
import { debug } from 'util';
import { IPictureData } from '../dynamic-module/lib/public_api';
import get from 'lodash.get';

export interface IPageMetaData {
  metaDescription?: IUProperty<string>;
  metaTitle?: IUProperty<string>;
  socialDescription?: IUProperty<string>;
  socialImage?: IUProperty<IPictureData>;
  socialTitle?: IUProperty<string>;
  microdata?: IUProperty<string>;
}

const SCHEMA_GENERATED_ID = 'schema-generated';
const SCHEMA_EDITOR_ATTRIBUTE = 'data-schema';

@Injectable({
  providedIn: 'root'
})
export class SEOService {
  private isInitialPage = true;
  private structuredDataRequest = 0;

  constructor(
    @Inject(DOCUMENT) private dom: Document,
    private meta: Meta,
    private http: HttpClient,
    private appConfigService: AppConfigService) { }

  addTags(metaData: IPageMetaData) {
    const meta = this.mapServerModel(metaData);

    meta.forEach(metaItem => {
      let selector = metaItem.name ? `name=${metaItem.name}` : `property='${metaItem.property}'`;
      this.meta.updateTag(metaItem, selector);
    });

    this.setEditorMicrodata(get(metaData, 'microdata.value', ''));
  }

  /**
   * The landing page gets its generated JSON-LD from the server (#schema-generated),
   * so it is only fetched again after client-side navigation.
   */
  updateStructuredData(url: string) {
    if (this.isInitialPage) {
      this.isInitialPage = false;
      return;
    }

    const request = ++this.structuredDataRequest;
    this.removeScripts(`#${SCHEMA_GENERATED_ID}`);

    const absoluteUrl = url.indexOf('://') > -1 ? url : this.appConfigService.getHostName() + url;
    this.http.get<string>('/ubaseline/api/bgStructuredData/getByUrl', { params: { url: absoluteUrl } })
      .subscribe(
        json => {
          if (!json || request !== this.structuredDataRequest) return;
          this.dom.head.appendChild(this.createJsonLdScript(json, { id: SCHEMA_GENERATED_ID }));
        },
        () => { });
  }

  private setEditorMicrodata(value: string) {
    this.removeScripts(`script[${SCHEMA_EDITOR_ATTRIBUTE}]`);

    const html = this.decodeMicrodata(value);
    if (!html) return;

    const container = this.dom.createElement('div');
    container.innerHTML = html;

    const scripts = Array.from(container.querySelectorAll('script[type="application/ld+json"]'));
    scripts.forEach(script => {
      const json = (script.textContent || '').trim();
      if (json) {
        this.dom.body.appendChild(this.createJsonLdScript(json, { [SCHEMA_EDITOR_ATTRIBUTE]: 'editor' }));
      }
    });
  }

  // The microdata property is stored JSON-encoded (the server renders it through Json.Decode).
  private decodeMicrodata(value: string): string {
    if (!value) return '';

    const trimmed = value.trim();
    if (trimmed.charAt(0) !== '"') return trimmed;

    try {
      return JSON.parse(trimmed);
    } catch {
      return '';
    }
  }

  private createJsonLdScript(json: string, attributes: { [name: string]: string }): HTMLScriptElement {
    const script = this.dom.createElement('script');
    script.type = 'application/ld+json';
    Object.keys(attributes).forEach(name => script.setAttribute(name, attributes[name]));
    script.textContent = json;

    return script;
  }

  private removeScripts(selector: string) {
    Array.from(this.dom.querySelectorAll(selector)).forEach(el => el.remove());
  }

  addCanonicalURL(url: string) {
    let link: HTMLLinkElement = this.dom.querySelector('[rel="canonical"]');
    if (!link) {
      link = this.dom.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.dom.head.appendChild(link);
    }

    let href = url.indexOf("://") > -1 ? url : this.appConfigService.getHostName() + url;
    link.setAttribute('href', href);
  }

  private mapServerModel(metaData: IPageMetaData) {
    let meta: MetaDefinition[] = [];

    if (!metaData) return meta;

    if (metaData.metaTitle) meta.push({ name: 'title', content: metaData.metaTitle.value || '' });
    if (metaData.metaDescription) meta.push({ name: 'description', content: metaData.metaDescription.value || '' });

    if (metaData.socialTitle) meta.push({ property: 'og:title', content: metaData.socialTitle.value || '' });
    if (metaData.socialDescription) meta.push({ property: 'og:description', content: metaData.socialDescription.value || '' });
      if (metaData.socialImage) meta.push({ property: 'og:image', content: get(metaData, "socialImage.value.sources[0].srcSet", '')});

    return meta;
  }
}
